import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { History as HistoryIcon, Car, Calendar, Clock, ChevronDown } from 'lucide-react';
import { api } from '@/lib/api';
import { formatTime, VEHICLE_LABELS } from '@/lib/utils';
import { Spinner } from '@/components/Spinner';
import EmptyState from '@/components/EmptyState';

interface WashRecord {
  id: string;
  washNumber: number;
  vehicleType: string;
  plateNumber: string;
  plateLetter: string;
  plateCity: string;
  vehicleBrand: string;
  vehicleColor: string;
  customerName: string;
  washedAt: string;
  washDurationMinutes: number | null;
  subscriptionOrderId: string;
}

interface HistoryPage {
  washes: WashRecord[];
  cursor: string | null;
  hasMore: boolean;
}

type VehicleFilter = 'all' | 'sedan' | 'suv' | 'large_suv';

export default function HistoryPage() {
  const [vehicleFilter, setVehicleFilter] = useState<VehicleFilter>('all');

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<HistoryPage>({
    queryKey: ['owner', 'history', vehicleFilter],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (vehicleFilter !== 'all') params.set('vehicleType', vehicleFilter);
      if (pageParam) params.set('cursor', pageParam as string);
      params.set('limit', '20');
      return api.get(`/owner/history?${params.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: undefined as string | undefined,
  });

  const allWashes = data?.pages.flatMap(page => page.washes) || [];

  // Group by date
  const grouped = allWashes.reduce<Record<string, WashRecord[]>>((acc, wash) => {
    const dateKey = new Date(wash.washedAt).toLocaleDateString('ar-IQ');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(wash);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">سجل الغسلات</h1>
          <p className="text-gray-500 text-sm mt-1">جميع الغسلات السابقة</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {[
          { key: 'all' as const, label: 'الكل' },
          { key: 'sedan' as const, label: 'سيدان' },
          { key: 'suv' as const, label: 'SUV' },
          { key: 'large_suv' as const, label: 'SUV كبير' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setVehicleFilter(tab.key)}
            className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              vehicleFilter === tab.key
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : allWashes.length === 0 ? (
        <EmptyState icon={HistoryIcon} title="لا توجد غسلات" description="ستظهر الغسلات هنا بعد تسجيلها" />
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, washes]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-bold text-gray-600">{date}</h3>
                <span className="text-xs text-gray-400">({washes.length} غسلة)</span>
              </div>
              <div className="space-y-2">
                {washes.map((wash) => (
                  <div key={wash.id} className="bg-white rounded-xl p-4 shadow-card">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                          <Car className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {wash.vehicleBrand} {wash.vehicleColor}
                          </p>
                          <p className="text-xs text-gray-500">
                            {wash.plateNumber} {wash.plateLetter} - {wash.plateCity}
                          </p>
                          {wash.customerName && (
                            <p className="text-xs text-gray-400 mt-0.5">{wash.customerName}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-left">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {VEHICLE_LABELS[wash.vehicleType]?.ar}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{formatTime(wash.washedAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span>غسلة #{wash.washNumber}</span>
                      {wash.washDurationMinutes && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {wash.washDurationMinutes} دقيقة
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Load More */}
          {hasNextPage && (
            <div className="text-center pt-2">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="btn-secondary inline-flex items-center gap-2"
              >
                {isFetchingNextPage ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    تحميل المزيد
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
